import { useEffect, useState } from 'react';
import {
  collection, doc, onSnapshot, orderBy, query, where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';
import { ymdInTimezone, toJsDate } from '../../lib/availability-helpers.js';

// Phase 7 of v1 spec — responder-side cover-requests page.
//
// Shows two sections:
//   1. "Covers I can pick up" — open requests where this user is in candidates[].
//      Each card has Volunteer + Decline buttons.
//   2. "My past requests" (collapsible) — requests this user CREATED, all statuses.
//      Useful for "did anyone pick up my Tuesday off?" check.
//
// Real-time via onSnapshot. Volunteer is a transactional callable — if two people
// click Volunteer at the same time, the loser sees "no longer open" and the UI
// snapshot will refresh to remove the card.

export default function CoverRequests() {
  const { user, claims } = useAuth();
  const vendorId = claims?.vendor_id;
  const myUid = user?.uid;

  const [vendor, setVendor] = useState(null); // for timezone
  const [openForMe, setOpenForMe] = useState([]); // open + I'm a candidate
  const [myRequests, setMyRequests] = useState([]); // I created (any status)
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    const unsub = onSnapshot(
      doc(db, 'vendors', vendorId),
      (snap) => setVendor(snap.exists() ? snap.data() : {}),
      () => {},
    );
    return () => unsub();
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId || !myUid) return;
    const q = query(
      collection(db, 'vendors', vendorId, 'coverRequests'),
      where('status', '==', 'open'),
      where('candidates', 'array-contains', myUid),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => setOpenForMe(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    return () => unsub();
  }, [vendorId, myUid]);

  useEffect(() => {
    if (!vendorId || !myUid) return;
    const q = query(
      collection(db, 'vendors', vendorId, 'coverRequests'),
      where('requestingResponderUid', '==', myUid),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => setMyRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {}, // ignore — non-essential section
    );
    return () => unsub();
  }, [vendorId, myUid]);

  const tz = vendor?.timezone || '';

  async function volunteer(requestId) {
    setError(null);
    setBusyId(requestId);
    try {
      await httpsCallable(functions, 'volunteerForCover')({ requestId });
      // The onSnapshot listener will remove the card automatically.
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function decline(requestId) {
    setError(null);
    setBusyId(requestId);
    try {
      await httpsCallable(functions, 'declineCoverRequest')({ requestId });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="cr-page">
      <h2>Cover requests</h2>

      {error && <div className="error-banner">{error}</div>}

      {/* === Open for me ========================================================== */}
      <section className="cr-section">
        <div className="cr-section-title">
          Covers you can pick up
          {openForMe.length > 0 && <span className="cr-count-badge">{openForMe.length}</span>}
        </div>
        {openForMe.length === 0 ? (
          <div className="placeholder">
            No open requests right now. You'll get a push if a teammate marks themselves out.
          </div>
        ) : (
          <ul className="cr-list">
            {openForMe.map((cr) => (
              <li key={cr.id} className="cr-card">
                <div className="cr-card-head">
                  <strong>{cr.requesterName || 'A teammate'}</strong> needs cover for{' '}
                  <strong>{ymdInTimezone(toJsDate(cr.affectedDate), tz)}</strong>
                </div>
                {cr.reason && (
                  <div className="cr-card-reason">Reason: {cr.reason}</div>
                )}
                <div className="cr-card-meta">
                  Posted {ageString(cr.createdAt)}
                </div>
                <div className="cr-card-actions">
                  <button
                    className="btn-primary"
                    onClick={() => volunteer(cr.id)}
                    disabled={busyId === cr.id}
                  >
                    {busyId === cr.id ? 'Claiming…' : 'Volunteer to cover'}
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() => decline(cr.id)}
                    disabled={busyId === cr.id}
                  >
                    Can't help
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* === My past requests ===================================================== */}
      <section className="cr-section">
        <div className="cr-section-title">My requests</div>
        {myRequests.length === 0 ? (
          <div className="placeholder">You haven't asked for cover yet.</div>
        ) : (
          <ul className="cr-list">
            {myRequests.map((cr) => (
              <li key={cr.id} className={`cr-card cr-card-status-${cr.status}`}>
                <div className="cr-card-head">
                  Cover for <strong>{ymdInTimezone(toJsDate(cr.affectedDate), tz)}</strong>
                  <span className={`cr-status-pill cr-status-pill-${cr.status}`}>{cr.status}</span>
                </div>
                {cr.reason && <div className="cr-card-reason">Reason: {cr.reason}</div>}
                {cr.status === 'covered' && (
                  <div className="cr-card-meta">
                    Picked up by <strong>{cr.coveringResponderName || 'a teammate'}</strong>
                  </div>
                )}
                {cr.status === 'open' && (
                  <div className="cr-card-meta">Asked {ageString(cr.createdAt)} · waiting for a volunteer</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---- helpers ---------------------------------------------------------------------

function ageString(ts) {
  const d = ts?.toDate?.();
  if (!d) return 'just now';
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
